import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { PsData } from '../types';

export class PsFormSelectionModel<DataType extends PsData> {
  /** Selection State */
  private readonly _selectionSubj = new BehaviorSubject(new Map<string, DataType>());
  public readonly selection$ = this._selectionSubj.asObservable().pipe(
    map(selection => Array.from(selection.values())),
    map(values => values.filter(value => !value.disabled)), // Ensure only enabled users
  );

  /** Size */
  public readonly size$ = this.selection$.pipe(map(selection => selection.length));
  public readonly hasSelection$ = this.size$.pipe(
    map(size => size > 0),
    distinctUntilChanged(),
  );

  /** Selected Hashes */
  public readonly selectedHashes$ = this.selection$.pipe(map(selection => selection.map(person => person.personHash)));

  constructor() {}

  public getSelection(): DataType[] {
    const selection = this._selectionSubj.getValue();
    return Array.from(selection.values()).filter(p => !p.disabled);
  }

  public getSelectionHashes(): string[] {
    return this.getSelection().map(person => person.personHash);
  }

  public getCurrentSize(): number {
    return this.getSelection().length;
  }

  public toggle(value: DataType): void {
    if (value?.disabled) return;
    this.isSelected(value) ? this.deselect(value) : this.select([value]);
  }

  public select(people: DataType[]): void {
    if (!people || !Array.isArray(people)) return;
    const selection = this._selectionSubj.getValue();
    people
      .filter(p => !p.disabled)
      .forEach(person => {
        selection.set(person.personHash, person);
      });
    this._selectionSubj.next(selection);
  }

  public deselect(...people: DataType[]): void {
    people.forEach(person => this._unmarkSelected(person));
  }

  public isSelected(value: DataType): boolean {
    if (!value || value.disabled) return false;
    const selection = this._selectionSubj.getValue();
    return selection.has(value.personHash);
  }

  public getSelected(personHash: string): DataType | undefined {
    const selection = this._selectionSubj.getValue();
    const person = selection.get(personHash);
    return person?.disabled ? undefined : person;
  }

  public clear(): void {
    this._unmarkAll();
  }

  private _unmarkSelected(person: DataType | undefined): void {
    if (!person) return;
    const selection = this._selectionSubj.getValue();
    selection.delete(person.personHash);
    this._selectionSubj.next(selection);
  }

  private _unmarkAll(): void {
    const selection = this._selectionSubj.getValue();
    if (selection.size === 0) return;
    selection.clear();
    this._selectionSubj.next(selection);
  }

  public cleanup(): void {
    this._selectionSubj.complete();
  }
}
